"use client";
import clsx from "clsx";
import type { InputHTMLAttributes } from "react";
import type { ControlSize } from "../../../lib/FrontendTypes";
import { useRadioGroup } from "./RadioGroup";
import "./Radio.css";

export interface RadioProps<TValue extends string = string>
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "size"> {
  value: TValue;
  onValueChange?: (value: TValue) => void;
  error?: string | boolean;
  size?: ControlSize;
}

/** A native radio input. Inside a `RadioGroup` it takes the group's name, selection and state. */
export default function Radio<TValue extends string = string>({
  value,
  name,
  checked,
  disabled,
  required,
  readOnly,
  error,
  size,
  onValueChange,
  onChange,
  onFocus,
  onBlur,
  className,
  ...props
}: RadioProps<TValue>) {
  const group = useRadioGroup();
  const resolvedReadOnly = readOnly ?? group?.readOnly ?? false;
  return (
    <input
      {...props}
      type="radio"
      className={clsx("calmlens-radio", className)}
      data-size={size ?? group?.size ?? "md"}
      value={value}
      name={name ?? group?.name}
      checked={checked ?? (group ? group.value === value : undefined)}
      disabled={disabled || group?.disabled}
      required={required ?? group?.required}
      aria-invalid={Boolean(error ?? group?.error) || undefined}
      onChange={(event) => {
        if (resolvedReadOnly) {
          event.preventDefault();
          return;
        }
        onValueChange?.(value);
        group?.onValueChange(value);
        onChange?.(event);
        group?.onChange?.(event);
      }}
      onFocus={(event) => {
        onFocus?.(event);
        group?.onFocus?.(event);
      }}
      onBlur={(event) => {
        onBlur?.(event);
        group?.onBlur?.(event);
      }}
    />
  );
}
